import { useMemo, useState } from "react";
import {
  ArrowRight,
  CalendarDays,
  ChevronRight,
  Flame,
  Search,
  TrendingUp,
  Trophy,
} from "lucide-react";
import { Link } from "react-router-dom";
import { AccumulatorCard } from "../components/AccumulatorCard";
import { BookmakerCard } from "../components/BookmakerCard";
import { CountrySelector } from "../components/CountrySelector";
import { SEO } from "../components/SEO";
import { TipCard } from "../components/TipCard";
import {
  DemoBadge,
  PrimaryLink,
  SectionHeading,
  SecondaryLink,
} from "../components/ui";
import { useCountry } from "../context/CountryContext";
import {
  accumulators,
  bookmakers,
  matches,
  popularSelections,
  tips,
} from "../data/demo";
import { combinedOdds, decimalReturn } from "../lib/calculators";

const dateFormat = new Intl.DateTimeFormat(undefined, {
  weekday: "long",
  month: "long",
  day: "numeric",
});

export function HomePage() {
  const { country } = useCountry();
  const [sport, setSport] = useState("Football");
  const [stake, setStake] = useState(100);
  const featured = tips.find((tip) => tip.featured) || tips[0];
  const acca = accumulators.find((item) => item.featured) || accumulators[0];
  const accaOdds = useMemo(
    () => combinedOdds(acca.selections.map((item) => item.odds)),
    [acca],
  );
  const availableBookmakers =
    country.code === "global"
      ? bookmakers
      : bookmakers.filter((bookmaker) =>
          bookmaker.countries.includes(country.code),
        );
  return (
    <>
      <SEO
        title="Today's sports picks, accumulators and betting sites"
        description="Discover today's featured sports picks, sample accumulators and country-specific bookmaker comparisons."
      />
      <section className="soft-grid relative overflow-hidden border-b border-soft py-12 sm:py-16 lg:py-24">
        <div className="absolute left-1/2 top-0 h-[430px] w-[430px] rounded-full bg-amber-300/15 blur-[110px]" />
        <div className="page-shell relative grid items-center gap-10 lg:grid-cols-[1.15fr_.85fr]">
          <div>
            <div className="mb-5 flex flex-wrap items-center gap-3">
              <DemoBadge />
              <span className="flex items-center gap-2 text-xs font-bold text-muted">
                <CalendarDays size={14} />
                {dateFormat.format(new Date())}
              </span>
            </div>
            <h1 className="display max-w-3xl text-4xl font-black leading-[1.04] sm:text-6xl lg:text-7xl">
              What are you <span className="text-lime">betting today?</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-7 text-muted sm:text-lg">
              Straightforward picks, popular matches and betting-site
              comparisons—built for a quick pre-match browse.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <PrimaryLink to="/tips">
                See today’s picks <ArrowRight size={17} />
              </PrimaryLink>
              <SecondaryLink to="/betting-sites">
                Compare betting sites
              </SecondaryLink>
            </div>
          </div>
          <div className="surface rounded-3xl p-5 sm:p-6">
            <div className="text-xs font-bold uppercase tracking-[.2em] text-lime">
              Set your feed
            </div>
            <h2 className="display mt-2 text-2xl font-black">
              Find today’s action
            </h2>
            <div className="mt-6 grid gap-3">
              <CountrySelector />
              <label className="rounded-xl border border-soft bg-white/[.045] px-4 py-3">
                <span className="sr-only">Sport</span>
                <select
                  value={sport}
                  onChange={(event) => setSport(event.target.value)}
                  className="w-full bg-transparent text-sm font-bold outline-none"
                >
                  <option className="bg-[#10241a]">Football</option>
                  <option className="bg-[#10241a]">Basketball</option>
                  <option className="bg-[#10241a]">Tennis</option>
                </select>
              </label>
              <Link
                to={`/tips?sport=${sport.toLowerCase()}`}
                className="focus-ring flex items-center justify-center gap-2 rounded-xl bg-lime px-4 py-3 font-extrabold"
              >
                <Search size={17} />
                Show today’s picks
              </Link>
            </div>
            <p className="mt-4 text-xs leading-5 text-muted">
              Country selection filters availability and payment details. Manual
              choice always takes priority.
            </p>
          </div>
        </div>
      </section>

      <section className="page-shell py-14 sm:py-20">
        <SectionHeading
          eyebrow="Fresh for today"
          title="🔥 Today’s Top Tips"
          description="Short, scannable picks with the market, publication odds and a plain-English reason."
          action={{ label: "View all tips", to: "/tips" }}
        />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {tips.slice(0, 3).map((tip) => (
            <TipCard tip={tip} key={tip.id} />
          ))}
        </div>
      </section>

      <section className="page-shell pb-14 sm:pb-20">
        <div className="overflow-hidden rounded-3xl border border-soft bg-[linear-gradient(135deg,#ffffff,#f4f6f5)] p-5 shadow-[0_12px_34px_rgba(19,41,29,.07)] sm:p-8">
          <div className="grid gap-7 lg:grid-cols-[.8fr_1.2fr] lg:items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-lime px-3 py-1.5 text-xs font-black uppercase tracking-[.15em] text-[#07110d]">
                <Trophy size={14} />
                Featured Pick
              </div>
              <h2 className="display mt-4 text-3xl font-black sm:text-4xl">
                Bet of the Day
              </h2>
              <p className="mt-3 max-w-md text-sm leading-6 text-muted">
                Our editorial team’s featured selection, shown with context—not
                a promise or guarantee.
              </p>
            </div>
            <TipCard tip={featured} featured />
          </div>
        </div>
      </section>

      <section className="border-y border-soft bg-white/[.015] py-14 sm:py-20">
        <div className="page-shell">
          <SectionHeading
            eyebrow="Build your matchday"
            title="🔥 Accumulator of the Day"
            description="Four demo selections in one easy-to-follow card. Returns are hypothetical."
            action={{ label: "All accumulators", to: "/accumulators" }}
          />
          <div className="grid gap-5 lg:grid-cols-[1.15fr_.85fr]">
            <AccumulatorCard accumulator={acca} />
            <div className="surface rounded-2xl p-6">
              <div className="text-xs font-bold uppercase tracking-[.18em] text-lime">
                Potential payout
              </div>
              <h3 className="display mt-2 text-2xl font-black">
                Try your stake
              </h3>
              <label className="mt-6 block text-xs font-bold uppercase tracking-wider text-muted">
                Stake ({country.currency})
                <input
                  className="mt-2 w-full rounded-xl border border-soft bg-white/[.04] px-4 py-3 text-lg font-bold outline-none focus:border-lime/50"
                  min="0"
                  step="10"
                  type="number"
                  value={stake}
                  onChange={(event) => setStake(Number(event.target.value))}
                />
              </label>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-white/[.04] p-4">
                  <div className="text-xs text-muted">Combined odds</div>
                  <div className="mt-1 text-2xl font-black">
                    {accaOdds.toFixed(2)}
                  </div>
                </div>
                <div className="rounded-xl bg-lime p-4 text-[#07110d]">
                  <div className="text-xs font-bold">Potential return</div>
                  <div className="mt-1 text-2xl font-black">
                    {country.currencySymbol}{" "}
                    {decimalReturn(stake, accaOdds).toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })}
                  </div>
                </div>
              </div>
              <p className="mt-4 text-xs leading-5 text-muted">
                Hypothetical return includes the stake. An accumulator loses if
                any non-void selection loses.
              </p>
              <PrimaryLink className="mt-5 w-full" to="/compare">
                Compare bookmakers <ChevronRight size={16} />
              </PrimaryLink>
            </div>
          </div>
        </div>
      </section>

      <section className="page-shell py-14 sm:py-20">
        <SectionHeading
          eyebrow="Sample interest signals"
          title="🔥 Popular Today"
          description="Demo ranking only. Real platform engagement will replace these sample trends after launch."
        />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {popularSelections.map((item, index) => (
            <div key={item.label} className="surface lift rounded-2xl p-5">
              <div className="flex items-center justify-between">
                <span className="grid h-8 w-8 place-items-center rounded-lg bg-white/[.05] text-sm font-black">
                  {index + 1}
                </span>
                <span className="flex items-center gap-1 text-xs font-bold text-emerald-300">
                  <TrendingUp size={14} />
                  {item.trend}
                </span>
              </div>
              <div className="mt-5 font-bold">{item.label}</div>
              <div className="mt-1 text-xs text-muted">{item.market}</div>
            </div>
          ))}
        </div>
        <div className="mt-4">
          <DemoBadge />
        </div>
      </section>

      <section className="page-shell pb-16">
        <SectionHeading
          eyebrow={`Available for ${country.name}`}
          title="Betting sites worth comparing"
          description="Editorial scores stay separate from sponsored placement and affiliate revenue."
          action={{ label: "Compare all sites", to: "/betting-sites" }}
        />
        {availableBookmakers.length ? (
          <div className="grid gap-4 lg:grid-cols-3">
            {availableBookmakers.slice(0, 3).map((bookmaker, index) => (
              <BookmakerCard
                bookmaker={bookmaker}
                rank={index + 1}
                key={bookmaker.id}
              />
            ))}
          </div>
        ) : (
          <div className="surface rounded-2xl p-8 text-center">
            <Flame className="mx-auto text-lime" />
            <p className="mt-3 font-bold">
              No bookmaker availability has been verified for this country yet.
            </p>
            <p className="mt-2 text-sm text-muted">
              Switch countries or check back after our next verification pass.
            </p>
          </div>
        )}
      </section>
    </>
  );
}
